// src/view/RecordsInput.js
import React, { useState } from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, } from '@mui/material';
// import { styled } from '@mui/material/styles';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { SectionTitle, SectionBody, Plain } from './StyledViews';
import MiscValue from '../value/MiscValue';
import { useAtom } from 'jotai';
import { stockDataAtom } from '../value/AppModel';
import AppUtil from '../util/AppUtil';
import { AppStyle } from '../value/AppConst';
import { useSnackbar } from 'notistack';

const StockColumns = [
    {id: 'date', label: '날짜', minWidth: 100, align:'center'},
    {id: 'open', label: '시가', minWidth: 100, align:'center'},
    {id: 'high', label: '고가', minWidth: 100, align:'center'},
    {id: 'low', label: '저가', minWidth: 100, align:'center'},
    {id: 'close', label: '종가', minWidth: 100, align:'center'},
    {id: 'volume', label: '거래량', minWidth: 100, align:'center'},
    // {id: 'rollingHigh', label: 'Rolling High', minWidth: 100, align:'center'},
];

export default function RecordsInput() {
    const [tsv, setTsv] = useState('');
    const [stockRecords, setStockRecords] = useAtom(stockDataAtom);
    const [errorCount, setErrorCount] = useState(0);
    const { enqueueSnackbar } = useSnackbar();

    const onClickPaste = () => {
        navigator.clipboard.readText().then(text => {
            if (text) {
                setTsv(text.trim());
                if(text.includes("Open") && text.includes("High") && text.includes("Low") && text.includes("Close") && text.includes("Volume") && text.includes("Date")) {
                    // 모든 필드가 포함된 경우
                    enqueueSnackbar("이제 '데이터 확인하기' 버튼을 눌러주세요.", { variant: 'info', autoHideDuration: 1200 });
                } else {
                    enqueueSnackbar("붙여넣은 데이터가 TSV 데이터 형식이 잘못되었습니다.", { variant: 'error', autoHideDuration: 2000 });
                }
            }
        });
    };

    const onClickParse = () => {
        if(tsv && tsv.length > 50) {
            try {
                const array = AppUtil.readStockData(tsv);
                setStockRecords(array);
                // Count errors using reduce.
                const errorCount = array.reduce((count, rec) => rec.error ? count + 1 : count, 0);
                setErrorCount(errorCount);
            } catch (error) {
                alert("데이터가 잘못되었습니다: " + error.message);
            }
        }
    };


    const renderStockRecords = () => {
        return(
            <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: 2 }}>
                <TableContainer sx={{ maxHeight: 440}}>
                    <Table stickyHeader aria-label="sticky table" size='small'>
                        <TableHead>
                            <TableRow>
                                {
                                    StockColumns.map(column => (
                                        <TableCell key={column.id} align={column.align}
                                            style={{ minWidth: column.minWidth, backgroundColor: AppStyle.SecondaryRGB, color: 'white' }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))
                                }
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                stockRecords.map((rec) => (
                                    <TableRow key={rec.date} hover style={{backgroundColor: rec.error ? '#ffcccc' : 'inherit'}}>
                                        {
                                            StockColumns.map(column => {
                                                var value = rec[column.id];
                                                if(column.id !== 'date') value = MiscValue.formatFloat(value);
                                                return (
                                                    <TableCell key={column.id} align={column.align}>
                                                        {value}
                                                    </TableCell>
                                                );
                                            })
                                        }
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    return (
        <Box>
            <SectionTitle label="주가 데이터 붙여넣기" />
            <SectionBody>
                <Box style={{display:'flex', marginTop: 16}}>
                    <Box style={{marginRight: 16, maxWidth: 340}}>
                        <Plain>
                            <a href="https://youtu.be/5kAWfmouqGE" target="_blank" rel="noopener noreferrer">유튜브 동영상
                            </a><span>에서 말씀드린 것처럼, 
                            구글 시트에 나타난 주가데이터를 모두 선택하고 Ctrl+C(맥북 Cmd+C)를 눌러 클립보드에 복사한 후 
                            아래의 주가 데이터 붙여넣기 버튼을 눌러주세요.</span><br/><br/>
                            <span>
                            붙여넣기를 눌렀을 때 브라우저에서 허용을 요청할 수 있습니다. 반드시 허용해 주세요.<br/><br/>
                            데이터가 입력되면 "데이터 확인하기" 버튼이 활성화됩니다. 그 버튼을 눌러 계속하세요.</span>
                        </Plain>
                    </Box>
                    <Box style={{flexGrow: 1, marginLeft: 16}}>
                        <TextField
                            multiline
                            value={tsv}
                            onChange={(e) => setTsv(e.target.value)}
                            rows={6}
                            variant="outlined"
                            placeholder="주가 데이터 붙여넣기 버튼을 눌러 주세요."
                            fullWidth
                            disabled={true}
                        />
                        <Box style={{display: 'flex', justifyContent: 'space-between', marginTop: 8}}>
                            <Button variant='contained'
                                color="info"
                                onClick={onClickPaste}
                                style={{minWidth: '28%'}}
                                startIcon={<ContentPasteIcon />}
                            >
                                주가 데이터 붙여넣기
                            </Button>
                            <Button variant='contained'
                                color="inherit"
                                disabled={!tsv}
                                onClick={() => setTsv('')}
                                style={{minWidth: '28%'}}
                                startIcon={<DeleteForeverIcon />}
                            >
                                데이터 리셋
                            </Button>
                            <Button variant='contained'
                                style={{ minWidth: '28%' }}
                                color="secondary"
                                onClick={onClickParse}
                                disabled={!tsv || tsv.length < 50}
                                startIcon={<ChecklistIcon />}
                            >
                                데이터 확인하기
                            </Button>
                        </Box>
                    </Box>
                </Box>
                {stockRecords.length > 0 && renderStockRecords()}
                <Box style={{
                    marginTop: 10, border: '1px solid '+AppStyle.PrimaryRGB, textAlign: 'center', padding: 10, borderRadius: 4
                    }}
                >
                    {
                        errorCount > 0
                        ?
                        <span>데이터에 {errorCount}개의 오류가 있습니다. 음영으로 표시된 데이터를 확인하세요.</span>
                        :
                        stockRecords.length > 0
                        ?
                        <span>데이터를 모두 확인했습니다. {stockRecords.length}개의 레코드가 있습니다. 매매규칙을 정하고 시뮬레이션을 진행할 수 있습니다.</span>
                        :
                        <span>아직 주가데이터가 입력되지 않았거나 검증되지 않았습니다. 데이터를 붙여넣은 후 "데이터 확인하기" 버튼을 눌러주세요.</span>
                    }
                </Box>
            </SectionBody>
        </Box>
    );
}

/*

[섹션 2: 주가 데이터 붙여넣기] RecordsInput.js
    - Multiline TextField를 크게 배치. (주가 데이터 붙여넣기 버튼 필요)
    - 버튼: "데이터 확인" 버튼을 클릭하면 사용자가 붙여넣은 텍스트 데이터를 파싱하여 날짜, 시가, 고가, 저가, 종가, 거래량 객체의 배열로 변환하는 함수를 실행하고 정보에 문제가 없는지 확인.
    - 차트: 데이터가 성공적으로 로드되면 여기에 캔들 차트를 표시.
    - 테이블: 데이터가 성공적으로 로드되면 테이블 형태로 날짜, 시가, 고가, 저가, 종가, 거래량을 표시.


Perl Script:


###
sub readStockData {
    my $rollingHighPeriod = shift; # 롤링 최고가 기간
    my %data;
    while(<DATA>) {
        chomp;
        if(/^\d{4}/) {
            my($date, $open, $high, $low, $close, $volume) = map {s/,//g; $_} split /\t/, $_;

            if($high < $low || $open < $low || $close < $low || $open > $high || $close > $high) {
                die "Invalid stock data: $_";
            }
            my($year, $month, $day) = $date =~ /(\d{4})\D+(\d+)\D+(\d+)/;
            $date = sprintf("%04d-%02d-%02d", $year, $month, $day);
            $data{$date} = {
                open => $open,
                high => $high,
                low => $low,
                close => $close,
                volume => $volume,
                rollingHigh => 0,
            };
        }
        elsif(/^\w/) {
            my($date, $open, $high, $low, $close, $volume) = split /\t/, $_;
            if($date ne 'Date' || $open ne 'Open' || $high ne 'High' || $low ne 'Low' || $close ne 'Close' || $volume ne 'Volume') {
                die "Invalid header: $_";
            }
        }
    }
    # 롤링 최고가 계산
    my @dates = sort keys %data;
    foreach my $i (0..$#dates) {
        if($i >= $rollingHighPeriod - 1 && $rollingHighPeriod > 0) {
            # close 가격의 롤링 최고가를 계산
            my $min = $i - $rollingHighPeriod + 1;
            my $high = 0;
            foreach my $k ($min..$i) {
                my $date = $dates[$k];
                if($data{$date}->{close} > $high) {
                    $high = $data{$date}->{close};
                }
            }
            $data{$dates[$i]}->{rollingHigh} = $high;
        }
    }
    \%data;
}

*/
